import dayjs from "dayjs";
import "dayjs/locale/nl";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isToday from "dayjs/plugin/isToday";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import i18next from "i18next";

const customThresholds = [
  { l: "s", r: 1 },
  { l: "m", r: 1 },
  { l: "mm", r: 59, d: "minute" },
  { l: "h", r: 1 },
  { l: "hh", r: 23, d: "hour" },
  { l: "d", r: 1 },
  { l: "dd", r: 29, d: "day" },
  { l: "M", r: 1 },
  { l: "MM", r: 11, d: "month" },
  { l: "y", r: 1 },
  { l: "yy", d: "year" },
];

const dayJsConfig = {
  customThresholds,
  rounding: Math.floor,
};

dayjs.extend(relativeTime, dayJsConfig);
dayjs.extend(customParseFormat);
dayjs.extend(isToday);
dayjs.extend(utc);

/**
 * EN-3282: the portal's canonical date + time format. 24-hour clock, date
 * first. Every textual date/time display uses this, and the patient-list
 * sorters parse the formatted strings back with the same constant, so the
 * emitter and the parsers cannot drift apart.
 */
export const LONG_DATE_TIME_FORMAT = "MMM DD, YYYY, HH:mm";

export function getLongFormattedDate(
  date: string | null | undefined,
  fallback: string = ""
) {
  if (!date) {
    return fallback;
  }

  const dateObj = new Date(date);

  return dayjs(dateObj).format(LONG_DATE_TIME_FORMAT);
}

export function getLongFormattedDateISO(date: string) {
  if (!date) {
    return "";
  }

  const dateObj = new Date(date).toISOString();

  return dayjs(dateObj).format(LONG_DATE_TIME_FORMAT);
}

export function getShortFormattedDate(date: string) {
  const dateObj = new Date(date);

  return dayjs(dateObj).format("MMM D, YYYY");
}

/**
 * Formats a calendar date (DOB, etc.) stored as UTC midnight without
 * converting to the viewer's timezone, which would shift the date back a
 * day for users west of UTC.
 */
export function getShortFormattedUtcDate(date: string) {
  return dayjs.utc(date).format("MMM D, YYYY");
}

/**
 * EN-5191: i18next language -> dayjs locale, for the relative-time strings only.
 *
 * Deliberately NOT `dayjs.locale(...)`. Setting the locale globally would also translate the
 * month abbreviation in LONG_DATE_TIME_FORMAT, and the patient-list sorters parse those emitted
 * strings back with that same constant — the emitter would move to Dutch while every parser and
 * every stored/compared string stayed English. Applying the locale per instance keeps the change
 * to the one output that is prose rather than data.
 *
 * A language absent from this map falls back to dayjs's built-in "en", which is what the portal
 * showed before this existed.
 */
const RELATIVE_TIME_LOCALES: Record<string, string> = {
  "nl-NL": "nl",
};

export function getRelativeTimeSinceDate(dateString: string) {
  // Parsed with the English format on purpose: the input is the output of getLongFormattedDate.
  const lastUsedDate = dayjs(dateString, LONG_DATE_TIME_FORMAT);
  const locale = RELATIVE_TIME_LOCALES[i18next.language] ?? "en";

  return lastUsedDate.locale(locale).fromNow();
}

/**
 * EN-5078: formats a medication dose time-of-day sent by the API as "HH:mm:ss"
 * into the portal's 24-hour form, e.g. "12:00", "14:00" (EN-3282). Returns the
 * fallback when the value is missing (legacy-regime rows carry no times).
 */
export function formatTimeOfDay(
  time: string | null | undefined,
  fallback: string = "-"
) {
  if (!time) {
    return fallback;
  }

  return dayjs(time, "HH:mm:ss").format("HH:mm");
}

/**
 * EN-5078: joined dose times for the medications table, e.g.
 * "12:00, 14:00". Returns the fallback when the medication has none.
 */
export function formatTimesOfDay(
  times: string[] | null | undefined,
  fallback: string = "-"
) {
  if (!times || times.length === 0) {
    return fallback;
  }

  return times.map((time) => formatTimeOfDay(time, fallback)).join(", ");
}

/**
 * EN-3282: timestamp under a chat bubble in the message thread. Relative by
 * design, the way a chat reads — a message sent today needs no date at all —
 * but anything outside the current year carries its year, because QA read
 * "07 May, 22:19" off a thread with no way to tell which May it was.
 */
export function getChatTimestamp(date: string | null | undefined) {
  if (!date) {
    return "";
  }

  const messageDate = dayjs(date);

  if (messageDate.isToday()) {
    return messageDate.format("HH:mm");
  }

  if (messageDate.year() === dayjs().year()) {
    return messageDate.format("DD MMM, HH:mm");
  }

  return messageDate.format("DD MMM YYYY, HH:mm");
}

/**
 * EN-3346: formats a protocol action date/time using a 24-hour clock (no
 * AM/PM), e.g. "Apr 08, 2022, 16:22". Returns "" when the date is missing so
 * the table renders an empty cell.
 */
export function getProtocolActionDateTime(date: string | null | undefined) {
  if (!date) {
    return "";
  }

  return dayjs(date).format(LONG_DATE_TIME_FORMAT);
}

export const formatLatestMeasurementDate = (latestDate: dayjs.Dayjs) => {
  if (!latestDate) return undefined;

  const now = dayjs();
  const date = dayjs(latestDate);
  const monthsDiff = now.diff(date, "month");

  if (monthsDiff <= 12) {
    return `${date.format("MMM DD")}, ${date.format("HH:mm")}`;
  }
  return `${date.format("MMM DD")}, ${date.format("YY")}'`;
};

export const formatLatestBloodPressure = (
  systolicMainValues: any,
  diastolicMainValues: any
) => {
  const systolicDate = systolicMainValues.latestDate
    ? dayjs(systolicMainValues.latestDate)
    : null;
  const diastolicDate = diastolicMainValues.latestDate
    ? dayjs(diastolicMainValues.latestDate)
    : null;

  if (systolicDate && diastolicDate && systolicDate.isSame(diastolicDate)) {
    return `${formatLatestMeasurementDate(systolicMainValues.latestDate)}`;
  }

  if (systolicDate && (!diastolicDate || systolicDate.isAfter(diastolicDate))) {
    return `${formatLatestMeasurementDate(systolicMainValues.latestDate)}`;
  }

  if (diastolicDate && (!systolicDate || diastolicDate.isAfter(systolicDate))) {
    return `${formatLatestMeasurementDate(diastolicMainValues.latestDate)}`;
  }

  return "";
};

/**
 * Formats a duration given in hours to a human-readable string (e.g., "2hr 30m").
 */
export function formatHoursAndMinutes(totalHours: number): string {
  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);

  if (hours === 0) {
    return `${minutes}m`;
  }
  if (minutes === 0) {
    return `${hours}hr`;
  }
  return `${hours}hr ${minutes}m`;
}

/**
 * Formats a duration given in minutes to a human-readable string (e.g., "2hr 30m").
 */
export function formatMinutesToHoursAndMinutes(
  totalMinutes: number | undefined | null
): string {
  if (totalMinutes === undefined || totalMinutes === null) {
    return "-";
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);

  if (hours === 0) {
    return `${minutes}m`;
  }
  if (minutes === 0) {
    return `${hours}hr`;
  }
  return `${hours}hr ${minutes}m`;
}
